<template>
  <div
      v-if="variant === 'panel'"
      :class="{ 'color-picker--no-palettes': hidePalettes }"
      class="color-picker--panel">
    <div
        v-if="activeObject && showGradient"
        class="cp-gradient">
      <GradientHue
          :active-object="activeObject"
          panel
          @updateSelectedPointOfGradient="updateSelectedPointOfGradient"
          @updateSelectedPointPosition="updateSelectedPointPosition"/>
      <div class="cp-angle">
        <span class="cp-angle__label">Angle</span>
        <input
            :value="activeObject.gradient.angle"
            :style="angleTrackStyle"
            min="0"
            step="1"
            max="90"
            type="range"
            class="cp-angle__range"
            aria-label="Angle"
            @input="updateGradientAngle($event.target.value)">
        <span class="cp-angle__value">{{ Math.round(activeObject.gradient.angle) }}</span>
      </div>
    </div>

    <Saturation
        ref="saturation"
        :color="rgbString"
        :hsv="hsv"
        panel
        @selectSaturation="selectSaturation"
    />
    <Hue
        ref="hue"
        :hsv="hsv"
        panel
        @selectHue="selectHue"
    />
    <Alpha
        v-if="!hideAlpha"
        ref="alpha"
        :color="rgbString"
        :rgba="rgba"
        panel
        @selectAlpha="selectAlpha"
    />

    <div class="cp-value-row">
      <Box
          :color="modelHex"
          name="RGBA"
          panel
          @inputColor="inputRgba">
        <Preview
            v-if="!hidePalettes"
            slot="prefix"
            :color="hexString"
            :width="22"
            :height="22"
            class="cp-preview"
        />
      </Box>
      <template v-if="isValidBrowser">
        <label
            v-if="eyedropper"
            class="cp-eyedropper"
            aria-label="Other"
            @click="showEyeDropper">
          <icon name="eyedropper"/>
        </label>
        <label
            v-else
            class="cp-eyedropper"
            aria-label="Other">
          <icon name="eyedropper"/>
          <input
              :value="hexString"
              type="color"
              style="width: 0px; height: 0px; visibility: hidden"
              @change="updateColorFromNativePicker"
              @input="updateColorFromNativePicker">
        </label>
      </template>
    </div>

    <template v-if="!hidePalettes">
      <div class="cp-divider"/>
      <div class="cp-group">
        <div class="cp-group__label">Recently Used Colors</div>
        <History @selectColor="selectColor"/>
      </div>
      <div class="cp-group">
        <div class="cp-group__label">Product Colors</div>
        <Colors
            :is-processing="productColorProcessing"
            :color="rgbaString"
            :colors-default="$store.getters.getProductColor"
            @selectColor="selectColor"
        />
      </div>
      <div class="cp-group">
        <div class="cp-group__label">Your Colors</div>
        <Colors
            :color="rgbaString"
            :colors-default="$store.getters.getAllYourColors"
            @selectColor="selectColor"
        />
      </div>
    </template>
  </div>
  <div
      v-else
      :class="getClass"
      class="color-picker" >
    <div class="first-column">
      <Saturation
          ref="saturation"
          :color="rgbString"
          :hsv="hsv"
          :size="hueHeight"
          @selectSaturation="selectSaturation"
      />
      <Hue
          ref="hue"
          :hsv="hsv"
          :width="hueWidth"
          :height="hueHeight"
          @selectHue="selectHue"
      />
      <Alpha
          v-if="!hideAlpha"
          ref="alpha"
          :color="rgbString"
          :rgba="rgba"
          :width="hueWidth"
          :height="hueHeight"
          @selectAlpha="selectAlpha"
      />
    </div>
    <div v-if="activeObject  && showGradient">
      <GradientHue :active-object="activeObject" @updateSelectedPointOfGradient="updateSelectedPointOfGradient"
                   @updateSelectedPointPosition="updateSelectedPointPosition"/>

      <div class="effects-subpanel">
        <div class="fs-12 "><label>Angle</label></div>
        <div class="effect-container">
          <input
              id="distance-range"
              min="0"
              step="1"
              max="90"
              :value="activeObject.gradient.angle"
              @input="updateGradientAngle($event.target.value)"
              type="range"
              class="slider rounded rounded-12 btn-outline-0"
          />
          <div class="box d-flex justify-content-center align-items-center">
            <span class="box-value">{{ Math.round(activeObject.gradient.angle) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="third-column">
      <div class="top-row tool-row">
        <div class="preview-tool">
          <div class="preview-title">Selected Color</div>
          <div class="preview-control">
            <Preview
                v-if="!hidePalettes"
                :color="hexString"
                :width="36"
                :height="36"
            />
            <Box
                :color="modelHex"
                name="RGBA"
                @inputColor="inputRgba"
            />
          </div>
        </div>
        <div
            v-if="isValidBrowser"
            class="other-tool tool-box">
          <div class="preview-title">Other</div>
          <div
              v-if="eyedropper"
              class="preview-control">
            <label
                class="bm-small-btns custom-icon d-flex align-items-center justify-content-center tooltip-button"
                @click="showEyeDropper"
            >
              <icon name="eyedropper"/>

            </label>
          </div>
          <div
              v-else
              class="preview-control">
            <label
                class="bm-small-btns custom-icon d-flex align-items-center justify-content-center tooltip-button"
            >
              <icon name="eyedropper"/>
              <input
                  :value="hexString"
                  type="color"
                  style="width: 0px; height: 0px; visibility: hidden"
                  @change="updateColorFromNativePicker"
                  @input="updateColorFromNativePicker">
            </label>
          </div>
        </div>
      </div>
      <div
          v-if="!hidePalettes"
          class="recently-used-color tool-row">
        <div class="preview-title">Recently Used Colors</div>
        <History @selectColor="selectColor"/>
      </div>
      <div
          v-if="!hidePalettes"
          class="your-color tool-row">
        <div class="preview-title">Product Colors</div>
        <Colors
            :is-processing="productColorProcessing"
            :color="rgbaString"
            :colors-default="$store.getters.getProductColor"
            @selectColor="selectColor"
        />
      </div>
      <div
          v-if="!hidePalettes"
          class="your-color tool-row">
        <div class="preview-title">Your Colors</div>
        <Colors
            :color="rgbaString"
            :colors-default="$store.getters.getAllYourColors"
            @selectColor="selectColor"
        />
      </div>

    </div>


  </div>
</template>

<script>
import EventBus from '@/EventBus';
import _ from 'lodash';
import Bowser from 'bowser';
import mixin from './mixin';
import Saturation from './Saturation.vue';
import Hue from './Hue.vue';
import Alpha from './Alpha.vue';
import Preview from './Preview.vue';
import Sucker from './Sucker.vue';
import Box from './Box.vue';
import Colors from './Colors.vue';
import History from './History.vue';
import GradientHue from "./GradientHue";

export default {
  components: {
    Saturation,
    Hue,
    Alpha,
    Preview,
    Sucker,
    Box,
    Colors,
    History,
    GradientHue
  },
  mixins: [mixin],
  props:  {
    color: {
      type:    String,
      default: '#000000',
    },
    theme: {
      type:    String,
      default: 'dark',
    },
    suckerHide: {
      type:    Boolean,
      default: true,
    },
    suckerCanvas: {
      type:    null, // HTMLCanvasElement
      default: null,
    },
    suckerArea: {
      type:    Array,
      default: () => [],
    },
    displayStyle: {
      type:    String,
      default: 'float',
    },
    colorsDefault: {
      type:    Array,
      default: () => [
        '#000000', '#FFFFFF', '#FF1900', '#F47365', '#FFB243', '#FFE623', '#6EFF2A', '#1BC7B1',
        '#00BEFF', '#2E81FF', '#5D61FF', '#FF89CF', '#FC3CAD', '#BF3DCE', '#8E00A7', 'rgba(0,0,0,0)',
      ],
    },
    colorsHistoryKey: {
      type:    String,
      default: 'bm-colorpicker-history',
    },
    activeObject:{
      type:    Object,
      default: () => {},
    },
    showGradient: { // Shadow effect for non-text objects
      type:    Boolean,
      default: false,
    },
    // Hides the recently used / product / your colour rows.
    hidePalettes: {
      type:    Boolean,
      default: false,
    },
    hideAlpha: {
      type:    Boolean,
      default: false,
    },
    // 'default' | 'panel' (left-panel look, see the first template block)
    variant: {
      type:    String,
      default: 'default',
    },
  },
  data() {
    return {
      hueWidth:               26,
      hueHeight:              200,
      previewHeight:          30,
      modelRgba:              '',
      modelHex:               '',
      r:                      0,
      g:                      0,
      b:                      0,
      a:                      1,
      h:                      0,
      s:                      0,
      v:                      0,
      productColorProcessing: false,
      eyedropper:             null,
      isValidBrowser:         false,
    };
  },
  computed: {
    getClass() {
      const classA = this.displayStyle === 'inline' ? 'inline-picker section-highlight' : '';
      const classB = this.activeObject && this.activeObject.ImageType && (this.activeObject.ImageType.indexOf('FDICText') !== -1 || this.activeObject.ImageType.indexOf('FDICLogo') !== -1) ? 'fdic-text-picker' : '';
      return `${classA} ${classB}`;
    },
    previewWidth() {
      return this.totalWidth - (this.suckerHide ? 0 : this.previewHeight);
    },
    rgba() {
      return {
        r: this.r,
        g: this.g,
        b: this.b,
        a: this.a,
      };
    },
    hsv() {
      return {
        h: this.h,
        s: this.s,
        v: this.v,
      };
    },
    rgbString() {
      return `rgb(${this.r}, ${this.g}, ${this.b})`;
    },
    rgbaStringShort() {
      return `${this.r}, ${this.g}, ${this.b}, ${this.a}`;
    },
    rgbaString() {
      return `rgba(${this.rgbaStringShort})`;
    },
    hexString() {
      return this.rgb2hex(this.rgba, true);
    },
    angleTrackStyle() {
      const angle   = (this.activeObject && this.activeObject.gradient && Number(this.activeObject.gradient.angle)) || 0;
      const percent = Math.min(100, Math.max(0, (angle / 90) * 100));
      return { background: `linear-gradient(to right, #1570EF ${percent}%, #D5D9E0 ${percent}%, #D5D9E0 100%)` };
    },
  },
  mounted() {
    EventBus.$on('StartProductColorProcessing', this.startProductColorProcessing);
    EventBus.$on('StopProductColorProcessing', this.stopProductColorProcessing);
    const browser       = Bowser.getParser(window.navigator.userAgent);
    this.isValidBrowser = browser.satisfies({
      // or in general
      chrome: '>=80',
      opera:  '>=22',
      edge:   '>=90',
    });
    try {
      this.eyedropper = new EyeDropper();
    } catch (ex) {
      console.log('Eyedropper cannot be init', ex);
    }
    console.log(`The current browser name is "${browser.getBrowserName()}" ver:${browser.getBrowserVersion()}`, `Your Eyedropper visibility will be ${this.isValidBrowser}`);
  },
  beforeDestroy() {
    EventBus.$off('StartProductColorProcessing');
    EventBus.$off('StopProductColorProcessing');
  },
  created() {
    Object.assign(this, this.setColorValue(this.color));
    this.setText();

    const vueObj = this;
    this.$watch('rgba', () => {
      this.emitColorChange(vueObj);
    });
  },

  methods: {
    emitColorChange: _.throttle((vueObj) => {
      console.log('Throttle button clicked!')
      vueObj.$emit('changeColor', {
        rgba: vueObj.rgba,
        hsv:  vueObj.hsv,
        hex:  vueObj.modelHex,
      });
    }, 300),
    updateGradientAngle(angle) {
      this.$emit('updateSelectedGradientAngle', angle);
    },
    updateSelectedPointPosition(position) {
      console.log('BMC ColorPicker file updateSelectedPointPosition', position);
      this.$emit('updateSelectedPointPosition', position);
    },
    updateSelectedPointOfGradient(index) {
      this.$emit('updateSelectedPointOfGradient', index);
    },
    startProductColorProcessing() {
      this.productColorProcessing = true;
    },
    stopProductColorProcessing() {
      this.productColorProcessing = false;
    },
    selectSaturation(color) {
      const {
        r, g, b, h, s, v,
      } = this.setColorValue(color);
      Object.assign(this, {
        r, g, b, h, s, v,
      });
      this.setText();
    },
    selectHue(color) {
      const {
        r, g, b, h, s, v,
      } = this.setColorValue(color);
      Object.assign(this, {
        r, g, b, h, s, v,
      });
      this.setText();
      this.$nextTick(() => {
        this.$refs.saturation.renderColor();
        this.$refs.saturation.renderSlide();
      });
    },
    showEyeDropper() {
      const that = this;
      this.eyedropper.open().then((colorSelectionResult) => {
        that.selectColor(colorSelectionResult.sRGBHex);
      })
          .catch((error) => {
            console.log(error);
          });
    },
    updateColorFromNativePicker(colorSelectionResult) {
      const that = this;
      that.selectColor(colorSelectionResult.target.value);
    },
    selectAlpha(a) {
      this.a = a;
      _.debounce(this.setText, 500);
    },
    inputHex(color) {
      const {
        r, g, b, a, h, s, v,
      } = this.setColorValue(color);
      Object.assign(this, {
        r, g, b, a, h, s, v,
      });
      this.modelHex  = color;
      this.modelRgba = this.rgbaStringShort;
      this.$nextTick(() => {
        this.$refs.saturation.renderColor();
        this.$refs.saturation.renderSlide();
        this.$refs.hue.renderSlide();
      });
    },
    inputRgba(color) {
      const {
        r, g, b, a, h, s, v,
      } = this.setColorValue(color);
      Object.assign(this, {
        r, g, b, a, h, s, v,
      });
      this.modelHex  = this.hexString;
      this.modelRgba = color;
      this.$nextTick(() => {
        this.$refs.saturation.renderColor();
        this.$refs.saturation.renderSlide();
        this.$refs.hue.renderSlide();
      });
    },
    setText() {
      this.modelHex  = this.hexString;
      this.modelRgba = this.rgbaStringShort;
    },
    selectColor(color) {
      console.log("Select color called")
      const {
        r, g, b, a, h, s, v,
      } = this.setColorValue(color);
      Object.assign(this, {
        r, g, b, a, h, s, v,
      });
      this.setText();
      this.$nextTick(() => {
        if(this.$refs.saturation) this.$refs.saturation.renderColor();
        if(this.$refs.saturation)  this.$refs.saturation.renderSlide();
        if(this.$refs.hue)  this.$refs.hue.renderSlide();
      });
    },
  },
};
</script>

<style lang="scss">

.color-picker {
  width: 600px;
  padding: 20px;
  background: white;
  border-radius: 12px !important;
  box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.16);
  z-index: 1;
  display: flex;
  position: fixed;
  &.inline-picker{
    padding: 5px 20px 0px 20px;

    &.fdic-text-picker{
      padding: 5px 00px 0px 00px;
    }
  }
  canvas{
    border-radius: 5px !important;
    border: solid thin #ededed;
  }
  .tool-row{
    margin-bottom: 5px;
    &:last-child{
      margin-bottom: 0px;
    }
  }
  .color-alert{
    font-size: 14px;
    color: red;
    margin-top: 8px;
    margin-left: 2px;
  }

  .first-column{
    display: flex;
    .saturation {
      position: relative;
      cursor: pointer;

      .slide {
        position: absolute;
        left: 100px;
        top: 0;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        border: 1px solid #fff;
        box-shadow: 0 0 1px 1px rgba(0, 0, 0, 0.3);
        pointer-events: none;
      }
    }
    .hue {
      position: relative;
      margin-left: 20px;
      cursor: pointer;
      width: 26px;
      .slide {
        position: absolute;
        left: -3px;
        top: 100px;
        right: -5px;
        height: 6px;
        border: solid 1px #202020;
        border-radius: 5px !important;

        pointer-events: none;
      }
    }
  }

  .color-show {
    margin-top: 8px;
    display: flex;
    .sucker {
      background: #eceef0;
    }
  }

  .third-column {
    margin-left: 20px;
    .color-type {
      display: flex;
      margin-left: 8px;
      .value {
        height: 36px;
        width: 84px;
        padding: 6px;
        color: #3F3F3F;
        border: solid thin #EDEDED;
        box-shadow: inset 0px 0px 6px 0px #EDEDED;
        border-radius: 5px;
        box-sizing: border-box;
        font-size: 12px;
        font-family: 'Open Sans';
      }
    }
    .other-tool{
      margin-left: 20px;
    }
    .preview-control{
      display: flex;
    }
    .preview-title {
      font-family: 'Open Sans';
      font-size: 12px;
      line-height: 20px;
      color: #5F5F5F;
      font-weight: 600;
      margin-bottom: 8px;
    }

    .top-row{
      display: flex;

    }
    .colors {
      padding: 0;
      margin: 0;
      > span {
        width: 20px;
        height: 20px;
        margin: 0 8px 6px 0;
        line-height: 20px;
        text-align: center;
        vertical-align: top;
      }
      .item {
        position: relative;
        width: 20px;
        height: 20px;
        margin: 0px 8px 6px 0px;
        border-radius: 3px;
        box-sizing: border-box;
        vertical-align: top;
        display: inline-block;
        transition: all 0.1s;
        cursor: pointer;
        border: solid thin #ccc;
        &:hover {
          transform: scale(1.4);
        }
        .alpha {
          height: 100%;
          border-radius: 4px; // 大一像素，否则四个角会看到白点
        }
        .color {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          border-radius: 3px;
        }
      }
    }
  }

  &.inline-picker{
    position: relative;
    flex-direction: column;
    width: 100%;
    box-shadow: none;
    .third-column{
      margin-left: 0px;
      padding: 10px 0px;
    }
  }

  .slider {
    &:focus {
      border: none;
    }

    -webkit-appearance: none;
    height: 4px;
    background: linear-gradient(90deg, rgb(214, 214, 214) 60%, rgb(214, 214, 214) 60%);
    opacity: 0.7;
    -webkit-transition: 0.2s;
    transition-opacity: 0.2s;
  }

  .effects-subpanel {
    .effect-container {
      display: flex;
      align-items: center;
      justify-content: space-between;

      input {
        width: 220px;
      }

      .box-effects {
        border-radius: 8px;
        font-size: large;
        width: 50px;
        height: 35px;
        background: white;
      }
    }
  }

  .box-name {
    margin-top: 5px;
    height: 20px;
    font-family: Open Sans;
    font-style: normal;
    font-weight: 600;
    font-size: 10px;
    line-height: 20px;
    text-transform: uppercase;

    color: #5F5F5F;

  }

  .box {
    background: #FFFFFF;
    border: 1px solid #EDEDED;
    box-sizing: border-box;
    box-shadow: inset 0px 0px 8px rgba(0, 0, 0, 0.08);
    border-radius: 4px;
    width: 60px;
    height: 36px;
  }

  .box-value {
    font-family: Open Sans;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 24px;
    text-align: center;
    color: #3F3F3F;
  }
  label.btn-picker-type.btn{
    &:checked{
      color: #007bff !important;
      border-color: #007bff;

    }

  }

}
</style>

<style lang="scss">
// Left-panel look (variant="panel"). Global because the parts are child components; every rule is
// scoped under .color-picker--panel (R7), so the default picker and its global rules are untouched.
@import '../../LeftSection/styles/lp-tokens';

.color-picker--panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  font-family: $lp-sans;
  color: $lp-ink;

  // Gradient bar + Angle sit at the top, the colour area below edits the selected stop
  .cp-gradient {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 14px;
  }

  .cp-angle {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .cp-angle__label {
    flex: 0 0 52px;
    font-size: $lp-fs-control;
    color: $lp-ink-2;
  }

  .cp-angle__range {
    flex: 1 1 auto;
    min-width: 0;
    height: 4px;
    margin: 0;
    border-radius: 4px;
    outline: none;
    cursor: pointer;
    -webkit-appearance: none;
    appearance: none;

    &::-webkit-slider-thumb {
      width: 16px;
      height: 16px;
      border: 2px solid $white;
      border-radius: 50%;
      background: $lp-accent;
      box-shadow: 0 1px 3px rgba(20, 22, 27, 0.3);
      cursor: pointer;
      -webkit-appearance: none;
    }

    &::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border: 2px solid $white;
      border-radius: 50%;
      background: $lp-accent;
      cursor: pointer;
    }

    &:focus-visible::-webkit-slider-thumb {
      box-shadow: 0 0 0 3px rgba(21, 112, 239, 0.25);
    }
  }

  .cp-angle__value {
    display: flex;
    flex: 0 0 44px;
    align-items: center;
    justify-content: center;
    height: 26px;
    border-radius: 6px;
    background: $lp-fill;
    font-family: $lp-mono !important; // beats the panel-wide Geist rule in _lp-global.scss
    font-size: $lp-fs-control;
    color: $lp-ink;
  }

  // Saturation / brightness box
  .cp-saturation {
    position: relative;
    height: 140px;
    cursor: pointer;

    canvas {
      display: block;
      width: 100%;
      height: 100%;
      border-radius: $lp-radius-tile;
    }
  }

  .cp-saturation__thumb {
    position: absolute;
    width: 18px;
    height: 18px;
    border: 2px solid $white;
    border-radius: 50%;
    box-shadow: 0 0 0 1px rgba(20, 22, 27, 0.35), 0 1px 4px rgba(20, 22, 27, 0.3);
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  // Hue + alpha bars
  .cp-bar {
    position: relative;
    height: 10px;
    margin-top: 12px;
    cursor: pointer;

    canvas {
      display: block;
      width: 100%;
      height: 100%;
      border-radius: 5px;
    }
  }

  .cp-bar--hue {
    margin-top: 16px;
  }

  .cp-bar__thumb {
    position: absolute;
    top: 50%;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: $white;
    box-shadow: 0 0 0 1px rgba(20, 22, 27, 0.14), 0 1px 3px rgba(20, 22, 27, 0.3);
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  // Hex field (+ selected colour chip) and eyedropper
  .cp-value-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
  }

  .cp-hex {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .cp-hex__field {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    gap: 8px;
    min-width: 0;
    height: 34px;
    margin: 0;
    padding: 0 10px;
    border-radius: $lp-radius-control;
    background: $lp-fill;
    cursor: text;
    transition: box-shadow 0.12s ease, background-color 0.12s ease;

    &.has-prefix {
      padding-left: 6px;
    }

    &:focus-within {
      background: $white;
      box-shadow: 0 0 0 1px $lp-accent, 0 0 0 4px rgba(21, 112, 239, 0.16);
    }
  }

  .cp-hex.is-invalid .cp-hex__field {
    background: $white;
    box-shadow: 0 0 0 1px #D92D20;
  }

  .cp-hex__input {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    height: 100%;
    padding: 0;
    border: 0;
    outline: none;
    background: transparent;
    font-family: $lp-mono !important; // beats the panel-wide Geist rule in _lp-global.scss
    font-size: $lp-fs-control;
    color: $lp-ink;
    text-transform: uppercase;
  }

  .cp-hex__alert {
    display: inline-flex;
    flex: 0 0 auto;
    color: #D92D20;
    cursor: help;
  }

  .cp-preview {
    flex: 0 0 auto;
    line-height: 0;

    canvas {
      display: block;
      width: 22px;
      height: 22px;
      border-radius: 6px;
      box-shadow: inset 0 0 0 1px rgba(20, 22, 27, 0.12);
    }
  }

  .cp-eyedropper {
    display: flex;
    flex: 0 0 34px;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin: 0;
    border-radius: $lp-radius-control;
    background: $lp-fill;
    cursor: pointer;

    &:hover {
      background: $lp-line;
    }

    .icon {
      min-width: 0;
      min-height: 0;
    }

    svg {
      width: 16px;
      height: 16px;

      path {
        stroke: $lp-ink-2;
      }
    }
  }

  // Recently used / Product / Your colours
  .cp-divider {
    height: 1px;
    margin: 14px 0 2px;
    background: $lp-line;
  }

  .cp-group {
    margin-top: 12px;
  }

  .cp-group__label {
    margin-bottom: 8px;
    font-size: $lp-fs-label;
    font-weight: 600;
    line-height: 16px;
    color: $lp-muted;
  }

  .colors {
    display: grid;
    grid-template-columns: repeat(8, 26px);
    justify-content: space-between;
    gap: 8px 0;
    margin: 0;
    padding: 0;
    list-style: none;

    > span {
      align-self: center;
      justify-self: center;
      line-height: 0;
    }

    .fa-refresh {
      display: inline-block;
      width: 16px;
      height: 16px;
      border: 2px solid $lp-line;
      border-top-color: $lp-accent;
      border-radius: 50%;

      &::before {
        content: none;
      }
    }

    .item {
      position: relative;
      width: 26px;
      height: 26px;
      margin: 0;
      border-radius: $lp-radius-control;
      cursor: pointer;
      transition: transform 0.12s ease, box-shadow 0.12s ease;

      &::after {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        border-radius: inherit;
        box-shadow: inset 0 0 0 1px rgba(20, 22, 27, 0.12);
        content: '';
        pointer-events: none;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 10px rgba(20, 22, 27, 0.18), 0 0 0 2px rgba(20, 22, 27, 0.06);
      }

      .alpha,
      .color {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        border-radius: inherit;
      }
    }
  }
}
</style>
